// ============================================================
// xScan - Contexto de escaneo (caja gris / caja blanca)
// ============================================================
// El modo NO se elige: se deriva de lo que el usuario aporta aqui.
// Espeja la logica de api/src/utils/scan-context.ts.
// ============================================================
import { useState } from 'react';
import {
  Network, FileJson, KeyRound, Cloud, Plus, Trash2, ShieldCheck, Info,
} from 'lucide-react';

export type ContextKind =
  | 'app_credentials'
  | 'api_spec'
  | 'network_inventory'
  | 'cloud_readonly';

export type ScanMode = 'blackbox' | 'greybox' | 'whitebox';

export interface ScanContextItem {
  kind: ContextKind;
  [key: string]: unknown;
}

const KIND_TO_MODE: Record<ContextKind, ScanMode> = {
  app_credentials: 'greybox',
  api_spec: 'greybox',
  network_inventory: 'greybox',
  cloud_readonly: 'whitebox',
};

const MODE_RANK: Record<ScanMode, number> = { blackbox: 0, greybox: 1, whitebox: 2 };

export function deriveScanMode(items: ScanContextItem[]): ScanMode {
  let mode: ScanMode = 'blackbox';
  for (const item of items) {
    const candidate = KIND_TO_MODE[item.kind];
    if (candidate && MODE_RANK[candidate] > MODE_RANK[mode]) mode = candidate;
  }
  return mode;
}

const KIND_META: Record<ContextKind, { label: string; desc: string; icon: typeof Network }> = {
  network_inventory: {
    label: 'Inventario de red',
    desc: 'Subredes y dominios internos. No requiere credenciales.',
    icon: Network,
  },
  api_spec: {
    label: 'Especificacion de API',
    desc: 'OpenAPI/Swagger. Evita que el escaneo tenga que adivinar endpoints.',
    icon: FileJson,
  },
  app_credentials: {
    label: 'Credenciales de aplicacion',
    desc: 'Usuario de prueba sin privilegios para escanear detras del login.',
    icon: KeyRound,
  },
  cloud_readonly: {
    label: 'Acceso cloud read-only',
    desc: 'Audita la configuracion de AWS o Azure (CSPM).',
    icon: Cloud,
  },
};

const MODE_STYLE: Record<ScanMode, { label: string; cls: string }> = {
  blackbox: { label: 'Caja negra', cls: 'bg-gray-100 text-gray-700 border-gray-300' },
  greybox: { label: 'Caja gris', cls: 'bg-blue-50 text-blue-700 border-blue-300' },
  whitebox: { label: 'Caja blanca', cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
};

interface Props {
  value: ScanContextItem[];
  onChange: (items: ScanContextItem[]) => void;
  disabled?: boolean;
}

const inputCls =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-300 disabled:bg-gray-100';

export default function ScanContextForm({ value, onChange, disabled }: Props) {
  const [adding, setAdding] = useState<ContextKind | ''>('');

  const mode = deriveScanMode(value);

  const addItem = (kind: ContextKind) => {
    const base: ScanContextItem =
      kind === 'cloud_readonly' ? { kind, provider: 'aws', region: 'us-east-1' } : { kind };
    onChange([...value, base]);
    setAdding('');
  };

  const updateItem = (index: number, patch: Record<string, unknown>) => {
    onChange(value.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const removeItem = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <ShieldCheck className="h-4 w-4 text-purple-500" />
            Contexto del objetivo
          </h3>
          <p className="mt-0.5 text-xs text-gray-500">
            Opcional. Aportar contexto convierte el escaneo en caja gris o blanca.
          </p>
        </div>
        <span className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium ${MODE_STYLE[mode].cls}`}>
          {MODE_STYLE[mode].label}
        </span>
      </div>

      {value.length === 0 && (
        <p className="mb-3 flex items-start gap-2 rounded-lg bg-gray-50 p-2.5 text-xs text-gray-600">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
          Sin contexto, el escaneo se ejecuta a ciegas desde afuera — igual que hasta ahora.
        </p>
      )}

      <div className="space-y-3">
        {value.map((item, index) => {
          const meta = KIND_META[item.kind];
          if (!meta) return null;
          const Icon = meta.icon;
          return (
            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm font-medium text-gray-800">
                  <Icon className="h-4 w-4 text-purple-500" />
                  {meta.label}
                </span>
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  disabled={disabled}
                  className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
                  aria-label={`Quitar ${meta.label}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>

              {item.kind === 'network_inventory' && (
                <div className="grid gap-2 sm:grid-cols-2">
                  <input
                    className={inputCls}
                    placeholder="Subredes: 10.0.0.0/24, 192.168.1.0/24"
                    disabled={disabled}
                    onChange={(e) =>
                      updateItem(index, {
                        subnets: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                      })
                    }
                  />
                  <input
                    className={inputCls}
                    placeholder="Dominios internos (opcional)"
                    disabled={disabled}
                    onChange={(e) =>
                      updateItem(index, {
                        internal_domains: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                      })
                    }
                  />
                </div>
              )}

              {item.kind === 'api_spec' && (
                <input
                  className={inputCls}
                  placeholder="https://api.ejemplo.com/openapi.json"
                  disabled={disabled}
                  onChange={(e) => updateItem(index, { spec_url: e.target.value })}
                />
              )}

              {item.kind === 'app_credentials' && (
                <div className="grid gap-2 sm:grid-cols-3">
                  <input
                    className={`${inputCls} sm:col-span-3`}
                    placeholder="URL de login: https://app.ejemplo.com/login"
                    disabled={disabled}
                    onChange={(e) => updateItem(index, { login_url: e.target.value })}
                  />
                  <input
                    className={`${inputCls} sm:col-span-2`}
                    placeholder="Usuario de prueba"
                    autoComplete="off"
                    disabled={disabled}
                    onChange={(e) => updateItem(index, { username: e.target.value })}
                  />
                  <input
                    className={inputCls}
                    type="password"
                    placeholder="Contrasena"
                    autoComplete="new-password"
                    disabled={disabled}
                    onChange={(e) => updateItem(index, { password: e.target.value })}
                  />
                </div>
              )}

              {item.kind === 'cloud_readonly' && (
                <CloudFields item={item} index={index} disabled={disabled} onUpdate={updateItem} />
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <select
          className={inputCls}
          value={adding}
          disabled={disabled}
          onChange={(e) => setAdding(e.target.value as ContextKind | '')}
        >
          <option value="">Agregar contexto...</option>
          {(Object.keys(KIND_META) as ContextKind[]).map((kind) => (
            <option key={kind} value={kind}>
              {KIND_META[kind].label} — {KIND_META[kind].desc}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => adding && addItem(adding)}
          disabled={disabled || !adding}
          className="flex shrink-0 items-center gap-1 rounded-lg bg-purple-500 px-3 py-2 text-sm font-medium text-white hover:bg-purple-600 disabled:opacity-40"
        >
          <Plus className="h-4 w-4" />
          Agregar
        </button>
      </div>
    </div>
  );
}

// ── Campos de cloud, separados para no inflar el componente principal ──

interface CloudFieldsProps {
  item: ScanContextItem;
  index: number;
  disabled?: boolean;
  onUpdate: (index: number, patch: Record<string, unknown>) => void;
}

function CloudFields({ item, index, disabled, onUpdate }: CloudFieldsProps) {
  const provider = (item.provider as string) || 'aws';
  const useRole = Boolean(item.role_arn);

  return (
    <div className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-2">
        <select
          className={inputCls}
          value={provider}
          disabled={disabled}
          onChange={(e) => onUpdate(index, { provider: e.target.value })}
        >
          <option value="aws">AWS</option>
          <option value="azure">Azure</option>
        </select>
        {provider === 'aws' && (
          <input
            className={inputCls}
            placeholder="Region (us-east-1)"
            defaultValue="us-east-1"
            disabled={disabled}
            onChange={(e) => onUpdate(index, { region: e.target.value })}
          />
        )}
      </div>

      {provider === 'aws' ? (
        <>
          <p className="text-xs text-gray-500">
            Recomendado: rol asumible con ExternalId — credenciales temporales, sin llaves de larga vida.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <input
              className={inputCls}
              placeholder="role_arn: arn:aws:iam::123:role/xscan-audit"
              disabled={disabled}
              onChange={(e) => onUpdate(index, { role_arn: e.target.value })}
            />
            <input
              className={inputCls}
              type="password"
              placeholder="external_id"
              autoComplete="off"
              disabled={disabled}
              onChange={(e) => onUpdate(index, { external_id: e.target.value })}
            />
          </div>
          {!useRole && (
            <div className="grid gap-2 sm:grid-cols-2">
              <input
                className={inputCls}
                placeholder="o access_key"
                autoComplete="off"
                disabled={disabled}
                onChange={(e) => onUpdate(index, { access_key: e.target.value })}
              />
              <input
                className={inputCls}
                type="password"
                placeholder="secret_key"
                autoComplete="new-password"
                disabled={disabled}
                onChange={(e) => onUpdate(index, { secret_key: e.target.value })}
              />
            </div>
          )}
        </>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          <input className={inputCls} placeholder="tenant_id" disabled={disabled}
            onChange={(e) => onUpdate(index, { tenant_id: e.target.value })} />
          <input className={inputCls} placeholder="client_id" disabled={disabled}
            onChange={(e) => onUpdate(index, { client_id: e.target.value })} />
          <input className={inputCls} type="password" placeholder="client_secret"
            autoComplete="new-password" disabled={disabled}
            onChange={(e) => onUpdate(index, { client_secret: e.target.value })} />
          <input className={inputCls} placeholder="subscription_id" disabled={disabled}
            onChange={(e) => onUpdate(index, { subscription_id: e.target.value })} />
        </div>
      )}
    </div>
  );
}
